import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
 Card,
 CardContent,
 CardDescription,
 CardHeader,
 CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/providers/AuthProvider';

export function ForgotPasswordPage() {
 const [step, setStep] = useState<'email' | 'reset'>('email');
 const [email, setEmail] = useState('');
 const [code, setCode] = useState('');
 const [newPassword, setNewPassword] = useState('');
 const [confirmPassword, setConfirmPassword] = useState('');
 const [isLoading, setIsLoading] = useState(false);
 const { forgotPassword, resetPassword } = useAuth();

 const handleSendCode = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!email) return;

  setIsLoading(true);
  try {
   await forgotPassword(email);
   setStep('reset');
  } catch (error) {
   // Error is handled by AuthProvider
  } finally {
   setIsLoading(false);
  }
 };

 const handleResetPassword = async (e: React.FormEvent) => {
  e.preventDefault();

  if (newPassword !== confirmPassword) {
   alert('Passwords do not match');
   return;
  }

  if (newPassword.length < 8) {
   alert('Password must be at least 8 characters long');
   return;
  }

  setIsLoading(true);
  try {
   await resetPassword(email, code, newPassword);
   // User will be automatically redirected after successful reset
  } catch (error) {
   // Error is handled by AuthProvider
  } finally {
   setIsLoading(false);
  }
 };

 return (
  <div className='min-h-screen flex items-center justify-center bg-background bg-grid-pattern py-12 px-4 sm:px-6 lg:px-8'>
   <div className='max-w-md w-full space-y-8'>
    <div className='text-center'>
     <h2 className='text-3xl font-extrabold text-foreground'>
      Reset your password
     </h2>
     <p className='mt-2 text-sm text-muted-foreground'>
      Remember your password?{' '}
      <Link
       to='/login'
       className='font-medium text-primary hover:text-primary/80'
      >
       Sign in
      </Link>
     </p>
    </div>

    <Card>
     {step === 'email' ? (
      <>
       <CardHeader>
        <CardTitle className='text-center'>Forgot Password</CardTitle>
        <CardDescription className='text-center'>
         Enter your email address and we'll send you a reset code
        </CardDescription>
       </CardHeader>
       <CardContent>
        <form onSubmit={handleSendCode} className='space-y-4'>
         <div className='space-y-2'>
          <Label htmlFor='email'>Email</Label>
          <Input
           id='email'
           type='email'
           placeholder='Enter your email'
           value={email}
           onChange={(e) => setEmail(e.target.value)}
           required
          />
         </div>

         <Button type='submit' className='w-full' disabled={isLoading}>
          {isLoading ? (
           <>
            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
            Sending code...
           </>
          ) : (
           'Send reset code'
          )}
         </Button>
        </form>
       </CardContent>
      </>
     ) : (
      <>
       <CardHeader>
        <CardTitle className='text-center'>Reset Password</CardTitle>
        <CardDescription className='text-center'>
         Enter the code sent to <strong>{email}</strong> and your new password
        </CardDescription>
       </CardHeader>
       <CardContent>
        <form onSubmit={handleResetPassword} className='space-y-4'>
         <div className='space-y-2'>
          <Label htmlFor='code'>Reset Code</Label>
          <Input
           id='code'
           type='text'
           placeholder='Enter 6-digit code'
           value={code}
           onChange={(e) =>
            setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
           }
           maxLength={6}
           className='text-center text-lg tracking-widest'
           required
          />
         </div>

         <div className='space-y-2'>
          <Label htmlFor='newPassword'>New Password</Label>
          <Input
           id='newPassword'
           type='password'
           placeholder='Enter new password (min. 8 characters)'
           value={newPassword}
           onChange={(e) => setNewPassword(e.target.value)}
           required
          />
         </div>

         <div className='space-y-2'>
          <Label htmlFor='confirmPassword'>Confirm Password</Label>
          <Input
           id='confirmPassword'
           type='password'
           placeholder='Confirm new password'
           value={confirmPassword}
           onChange={(e) => setConfirmPassword(e.target.value)}
           required
          />
         </div>

         <Button
          type='submit'
          className='w-full'
          disabled={isLoading || code.length !== 6}
         >
          {isLoading ? (
           <>
            <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
            Resetting password...
           </>
          ) : (
           'Reset password'
          )}
         </Button>

         <div className='text-center'>
          <Button
           type='button'
           variant='ghost'
           onClick={() => setStep('email')}
           className='text-sm'
          >
           Back to email
          </Button>
         </div>
        </form>
       </CardContent>
      </>
     )}
    </Card>
   </div>
  </div>
 );
}
